import { type NavigationGroup } from "../../types";
import { generateId } from "../../utils";
import {
  loadProjectIntoState,
} from "./project-slice-helpers";
import type {
  ProjectSlice,
  ProjectSliceState,
} from "./project-slice-types";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";

export type { ProjectSlice } from "./project-slice-types";

export function createProjectSlice<TState extends ProjectSliceState>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): ProjectSlice {
  return {
    setProject: (project) => {
      set((state) => {
        loadProjectIntoState(state, project);
      });

      get().syncWithVersioningStore();
    },

    saveProject: async () => {
      if (!get().project) {
        return;
      }

      try {
        set((state) => {
          state.isModified = false;
          state.lastSaved = new Date();
        });
      } catch (error) {
        console.error("Failed to save project:", error);
        throw error;
      }
    },

    revalidateProject: async () => {
      const { project } = get();
      if (!project) {
        return;
      }

      try {
        const { ProjectsApi } = await import("../../services/api/projects");
        const response = await ProjectsApi.getProject(project.id);

        if (response.success && response.data) {
          get().setProject(response.data);
          return;
        }

        console.error("❌ Project revalidation failed:", response.message);
        throw new Error(response.message || "Failed to revalidate project");
      } catch (error) {
        console.error("❌ Project revalidation error:", error);

        set((state) => {
          state.project = null;
          state.components = [];
          state.componentMap = new Map();
          state.selectedComponents = [];
          state.pageComponents = new Map();
          state.pages = [];
          state.pageMap = new Map();
          state.currentPageId = undefined;
          state.currentPage = null;
          state.isModified = false;
          state.lastSaved = null;
        });

        throw error;
      }
    },

    appendProjectVersion: (version) => {
      set((state) => {
        if (!state.project) {
          return;
        }

        const exists = state.project.versions.some(
          (candidate) => candidate.version === version.version,
        );
        if (!exists) {
          state.project.versions.push(version);
        }

        state.project.currentVersion = version.version;
      });
    },

    setProjectCurrentVersion: (versionNumber) => {
      set((state) => {
        if (state.project) {
          state.project.currentVersion = versionNumber;
        }
      });
    },

    updateNavigationSettings: (settings) => {
      set((state) => {
        if (!state.project) {
          return;
        }

        state.project.navigation = {
          ...state.project.navigation,
          ...settings,
        };
        state.isModified = true;
      });
    },

    addNavigationGroup: (group) => {
      set((state) => {
        if (!state.project) {
          return;
        }

        const newGroup: NavigationGroup = {
          ...group,
          id: generateId("nav-group"),
        };

        state.project.navigation.navigationGroups.push(newGroup);
        state.isModified = true;
      });
    },

    updateNavigationGroup: (groupId, updates) => {
      set((state) => {
        if (!state.project) {
          return;
        }

        const groupIndex = state.project.navigation.navigationGroups.findIndex(
          (group) => group.id === groupId,
        );
        if (groupIndex === -1) {
          return;
        }

        state.project.navigation.navigationGroups[groupIndex] = {
          ...state.project.navigation.navigationGroups[groupIndex],
          ...updates,
        };
        state.isModified = true;
      });
    },

    deleteNavigationGroup: (groupId) => {
      set((state) => {
        if (!state.project) {
          return;
        }

        state.project.navigation.navigationGroups =
          state.project.navigation.navigationGroups.filter(
            (group) => group.id !== groupId,
          );

        for (const page of state.pages) {
          if (page.navigation?.navigationGroup === groupId) {
            page.navigation.navigationGroup = undefined;
            const mapPage = state.pageMap.get(page.id);
            if (mapPage) {
              mapPage.navigation.navigationGroup = undefined;
            }
          }
        }

        if (state.currentPage?.navigation?.navigationGroup === groupId) {
          state.currentPage.navigation.navigationGroup = undefined;
        }

        state.isModified = true;
      });
    },
  };
}
